import { cn } from '@h2hspark/ui';
import { ChevronRight, Lock } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useOverlayLinkState } from './attendee-detail-dialog';
import { fullName, initialsOf, privateAttendeesLines, roleAtCompany } from './constants';
import type { AttendeePreviewCard, PrivateAttendees } from './types';

/**
 * The preview card (`MEM-011` AC-6) — the smallest public representation of a member inside an
 * event.
 *
 * ## Transcribed from `Member App.dc.html`, the Who's coming rows
 *
 * A `--surface-card` row with a `1px --border-subtle` border, `--radius-lg`, `--shadow-xs` and
 * 12px padding; a 44px `999px` initials tile at `font-brand 700/15px`; the name at
 * `font-brand 700/var(--text-sm)` in `--text-strong`; the role line at `var(--text-xs)` in
 * `--text-muted`; and the conversation starter at `var(--text-xs)` in `--brand-primary`,
 * **in curly quotes**, which is the design's own treatment and the thing that turns a list of job
 * titles into something a person can act on.
 *
 * ## What the prototype draws and this does not
 *
 * **The green presence dot** on the avatar. In the markup it is a static decoration on two hard
 * coded rows; there is no online-presence signal in this product and inventing one from
 * `stillAttending` would tell a member somebody is in the room.
 *
 * ## Why the whole row is one link and not a card with a button in it
 *
 * `MEM-025` AC-3 wants the person's region to be "a selectable region opening the detail", and the
 * prototype puts `onClick` on the row. One `<Link>` gives that a real focus stop, a real name and
 * keyboard behaviour for free (NFR `accessibility#wcag-aa`), where a `<div onClick>` gives none of
 * the three.
 */
/**
 * **How wide an attendee card's action column is, at every width.**
 *
 * The longest labels the pair can hold are *Save to Meet* and *Request sent* — twelve characters
 * each, plus a 16px glyph and a 6px gap — which measures 138px at `--text-sm`. 144px carries them
 * with a hair to spare and is the same on a phone, where the card is the full column width and has
 * the room; a narrower phone value would make the width itself a breakpoint, and the row would
 * still move, one line lower.
 *
 * Exported so a test can assert the pair share it rather than each having been sized by eye.
 */
export const ACTION_COLUMN_CLASS = 'w-36';

export function AttendeeCard({
  card,
  to,
  actions,
}: {
  readonly card: AttendeePreviewCard;
  readonly to: string;
  /**
   * §3.9's stacked pair at the right of the row — Save to Meet over Connect — on the Matches
   * screen's *Everyone attending* grid (`MEM-025` AC-7, `MEM-032` AC-2).
   *
   * **A sibling of the link, never inside it.** The prototype puts its `onClick` on the avatar and
   * the text block and leaves the buttons outside, and the markup has to do the same: a `<button>`
   * nested in an `<a>` is invalid and the keyboard behaviour of the pair is undefined. Without
   * actions the row keeps the roster's chevron, which is what the link alone affords.
   */
  readonly actions?: React.ReactNode;
}): React.ReactElement {
  const line = roleAtCompany(card);
  /* §2.16 opens the person **over** this list rather than instead of it, and the way a link says
     so is by carrying the list's own location. The address is unchanged — see
     `attendee-detail-dialog.tsx`. */
  const overlayState = useOverlayLinkState();
  return (
    <article className="flex min-w-0 items-center gap-3.5 rounded-lg border border-border/60 bg-card p-3 shadow-xs md:p-4">
      <Link to={to} state={overlayState} className="flex min-w-0 flex-1 items-center gap-3.5">
        <span
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-[15px] font-bold text-brand-700 md:h-[46px] md:w-[46px]"
          aria-hidden
        >
          {initialsOf(card)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-brand text-sm font-bold text-foreground md:text-md">
            {fullName(card)}
          </span>
          {line === null ? null : (
            <span className="block truncate text-xs text-muted-foreground md:text-sm">{line}</span>
          )}
          {card.conversationStarter === null ? null : (
            <span className="mt-0.5 block truncate text-xs text-brand">
              &ldquo;{card.conversationStarter}&rdquo;
            </span>
          )}
        </span>
      </Link>
      {actions === undefined ? (
        <ChevronRight className="h-4 w-4 shrink-0 text-text-subtle" aria-hidden />
      ) : (
        /* **A fixed footprint, so a state change never moves the row** — Sumit, reviewing #529
           (ruling 28). *Save to Meet* → *On list* and *Connect* → *Request sent* → *Connected* are
           labels of different lengths, and with the column sized to its content every tap resized
           it and shifted the name beside it. The width is `ACTION_COLUMN_CLASS`, sized to the
           longest label at this type size, and each control fills it. */
        <span className={cn('flex flex-none flex-col gap-[7px]', ACTION_COLUMN_CLASS)}>
          {actions}
        </span>
      )}
    </article>
  );
}

/**
 * `MEM-011` AC-5 — the private-attendee row, **at the end** and never in the list.
 *
 * It is a sibling of the list rather than its last item, which is what the API's shape already
 * decided: `privateAttendees` is a field beside `attendees`, so nothing can sort this into the
 * middle and nothing can mistake it for a person.
 *
 * The dashed border and the sunken surface are the prototype's own, and they are doing work: this
 * row looks like a note about the list rather than an entry in it.
 *
 * Renders nothing at zero. "0 attendees are private" is not a state — the API sends `notice: null`
 * and this returns `null` rather than drawing an empty explanation.
 */
export function PrivateAttendeesRow({
  privateAttendees,
  className,
}: {
  readonly privateAttendees: PrivateAttendees;
  /** `col-span-full` on the Matches grid, where §2.4 spans the row across both columns. */
  readonly className?: string;
}): React.ReactElement | null {
  if (privateAttendees.count === 0 || privateAttendees.notice === null) return null;
  const { headline, detail } = privateAttendeesLines(privateAttendees.notice);
  return (
    <li
      className={cn(
        'flex items-center gap-3 rounded-lg border border-dashed border-border bg-muted/50 p-3 md:gap-3.5 md:p-4',
        className,
      )}
    >
      <span
        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-pill border border-border bg-card text-text-subtle"
        aria-hidden
      >
        <Lock className="h-[18px] w-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-brand text-sm font-bold text-text-body">{headline}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
    </li>
  );
}
